import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';
import { useAppContext } from '../context/AppContext';

const Settings = () => {
  const { setInstitute } = useAppContext();
  const [thisInstitute, setThisInstitute] = useState({
    name: '',
    code: '',
    address: '',
    mobile: '',
    email: '',
    website: '',
    demo: 0,
    curr_name: '',
    curr_short_name: '',
    curr_basic_name: '',
    curr_basic_short_name: '',
    decimal_point: 2,
    round_off: 'No',
    academic_year_start: '',
    receipt_vno_start: 1,
    payment_vno_start: 1,
    journal_vno_start: 1,
    contra_vno_start: 1,
    id_bg: '',
  });

  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false); // ✅ modal toggle

  useEffect(() => {
    fetchInstituteDetails();
  }, []);

  const fetchInstituteDetails = async () => {
    setLoading(true);
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(
        `${appConstants.API_ROOT_URL}get-my-institute`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      const data = await response.json();
      if (response.ok) {
        setThisInstitute(data.data);
        setInstitute(data.data);
      } else {
        setError(data.message || 'Failed to fetch institute details.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadImage = async () => {
    if (!selectedFile) {
      alert('Please select an image first.');
      return;
    }
    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const authToken = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('id_bg', selectedFile);

      const response = await fetch(`${appConstants.API_ROOT_URL}upload-id-bg`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: formData,
      });

      // 🔒 Check if the response has valid JSON
      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error('Invalid JSON response from server.');
      }

      if (response.ok && data.status === 'success') {
        setSuccess(data.message || 'Background updated successfully!');
        setThisInstitute((prev) => ({
          ...prev,
          id_bg: data.file_name,
        }));
        setSelectedFile(null);
        setShowModal(false); // ✅ Close modal cleanly
        await fetchInstituteDetails();
      } else {
        alert(data.message || 'Failed to upload image.');
      }
    } catch (err) {
      alert('Network error during upload: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (e) => {
    setThisInstitute({ ...thisInstitute, [e.target.name]: e.target.value });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(
        `${appConstants.API_ROOT_URL}update-institute`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify(thisInstitute),
        },
      );

      const data = await response.json();
      if (response.ok) {
        setSuccess('Institute details updated successfully!');
        await fetchInstituteDetails();
      } else {
        setError(data.message || 'Failed to update institute details.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Layout>
      <div className="container mt-4">
        <h2 className="text-center mb-4">Institute Settings</h2>

        {loading && (
          <div className="alert alert-info text-center">Loading...</div>
        )}
        {error && <div className="alert alert-danger text-center">{error}</div>}
        {success && (
          <div className="alert alert-success text-center">{success}</div>
        )}

        {!loading && (
          <div className="row">
            {/* Left column — form */}
            <div className="col-md-6 mb-4">
              <div className="card p-4 shadow-sm">
                <form onSubmit={handleUpdate}>
                  {Object.keys(thisInstitute)
                    .filter(
                      (key) => !['id', 'demo', 'status', 'id_bg'].includes(key),
                    )
                    .map((key) => (
                      <div className="mb-3" key={key}>
                        <label className="form-label">
                          {key.replace(/_/g, ' ').toUpperCase()}
                        </label>
                        <input
                          type={
                            typeof thisInstitute[key] === 'number'
                              ? 'number'
                              : 'text'
                          }
                          className="form-control"
                          name={key}
                          value={thisInstitute[key]}
                          onChange={handleChange}
                          disabled={key === 'code'}
                        />
                      </div>
                    ))}

                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={updating}
                  >
                    {updating ? 'Updating...' : 'Update'}
                  </button>
                </form>
              </div>
            </div>

            {/* Right column — Image */}
            <div className="col-md-6 text-center">
              <h5 className="mb-3">ID Card Background</h5>

              {thisInstitute.id_bg ? (
                <img
                  src={`${appConstants.API_ROOT_URL}uploads/${thisInstitute.id_bg}`}
                  alt="ID Card Background"
                  style={{
                    width: '100%',
                    maxWidth: '500px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    window.open(
                      `${appConstants.API_ROOT_URL}uploads/${thisInstitute.id_bg}`,
                      '_blank',
                    )
                  }
                />
              ) : (
                <div className="text-muted mb-3">
                  No background image uploaded
                </div>
              )}

              <button
                type="button"
                className="btn btn-secondary mt-3"
                onClick={() => setShowModal(true)}
              >
                Change Background
              </button>
            </div>
          </div>
        )}

        {/* ✅ Modal (React-controlled) */}
        {showModal && (
          <div
            className="modal fade show"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Change ID Card Background</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  ></button>
                </div>
                <div className="modal-body text-center">
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control mb-3"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                  />
                  {selectedFile && (
                    <img
                      src={URL.createObjectURL(selectedFile)}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        borderRadius: '10px',
                      }}
                    />
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={uploading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleUploadImage}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Settings;
